import { Description, MoreOutline, Projects, Section, X } from '@doist/icons/product-ui';
import { Text } from '@doist/marketist';
import cn from 'clsx';
import { useTranslation } from 'next-i18next';
import { ProductUIBoxShadow } from '../product-ui-box-shadow/product-ui-box-shadow';
import { ProductUISidebarHeaderItem } from '../product-ui-list-item';
import { ProductUISkeletonText } from '../product-ui-skeleton-text/product-ui-skeleton-text';
import { ProductUITaskAttributeButton } from '../product-ui-task-attribute-button/product-ui-task-attribute-button';
import css from './product-ui-task-view.module.css';
import type { ReactNode } from 'react';
type ProductUITaskViewProps = {
  children: ReactNode;
  subtasksSection?: ReactNode;
  comments?: ReactNode[];
  commentField?: ReactNode;
  sidebarSections?: ReactNode[];
};
function ProductUITaskView({
  children,
  subtasksSection,
  comments = [],
  commentField = null,
  sidebarSections
}: ProductUITaskViewProps) {
  const {
    t
  } = useTranslation('productui');
  return <div className={css.container} data-sentry-component="ProductUITaskView" data-sentry-source-file="product-ui-task-view.tsx">
            <ProductUIBoxShadow data-sentry-element="ProductUIBoxShadow" data-sentry-source-file="product-ui-task-view.tsx" />
            <ProductUITaskViewHeader data-sentry-element="ProductUITaskViewHeader" data-sentry-source-file="product-ui-task-view.tsx" />
            <div className={css.content}>
                <div className={css.main}>
                    {children}
                    {subtasksSection}
                    <div className={css.comments}>
                        {comments.length ? <>
                                <div className={css.title}>
                                    <Text size="minor-xs" weight="medium" tag="span">
                                        {t('comments')}
                                    </Text>
                                    <ProductUISkeletonText width="5" />
                                </div>
                                {comments}
                            </> : null}

                        {commentField}
                    </div>
                </div>
                <div className={css.sidebar}>{sidebarSections}</div>
            </div>
        </div>;
}
function ProductUITaskViewHeader() {
  return <div className={css.header} data-sentry-component="ProductUITaskViewHeader" data-sentry-source-file="product-ui-task-view.tsx">
            <div className={css.left}>
                <Projects width={20} height={20} color={'var(--productui-meta-red)'} data-sentry-element="Projects" data-sentry-source-file="product-ui-task-view.tsx" />
                <ProductUISkeletonText width="100" data-sentry-element="ProductUISkeletonText" data-sentry-source-file="product-ui-task-view.tsx" />
                <Text tag="span" className={css.tertiary} size="body-xs" data-sentry-element="Text" data-sentry-source-file="product-ui-task-view.tsx">
                    /
                </Text>
                <Section width={20} height={20} color={'var(--display-onlight-tertiary)'} data-sentry-element="Section" data-sentry-source-file="product-ui-task-view.tsx" />
                <ProductUISkeletonText width="100" data-sentry-element="ProductUISkeletonText" data-sentry-source-file="product-ui-task-view.tsx" />
            </div>
            <div className={css.right}>
                <ProductUITaskAttributeButton size="lg" data-sentry-element="ProductUITaskAttributeButton" data-sentry-source-file="product-ui-task-view.tsx">
                    <MoreOutline data-sentry-element="MoreOutline" data-sentry-source-file="product-ui-task-view.tsx" />
                </ProductUITaskAttributeButton>
                <ProductUITaskAttributeButton size="lg" data-sentry-element="ProductUITaskAttributeButton" data-sentry-source-file="product-ui-task-view.tsx">
                    <X data-sentry-element="X" data-sentry-source-file="product-ui-task-view.tsx" />
                </ProductUITaskAttributeButton>
            </div>
        </div>;
}
type ProductUITaskViewSidebarSectionProps = {
  label: string;
  children?: ReactNode;
};
ProductUITaskView.SideBarSection = ({
  label,
  children
}: ProductUITaskViewSidebarSectionProps) => {
  return <div className={css.sideBarSection}>
            <ProductUISidebarHeaderItem size="sm" label={label} />
            {children}
        </div>;
};
type ProductUITaskViewTaskProps = {
  taskCircle: ReactNode;
  name: string;
  description?: string;
  placeholder?: string;
};
ProductUITaskView.Task = ({
  taskCircle,
  name,
  description = '',
  placeholder = ''
}: ProductUITaskViewTaskProps) => {
  return <div className={css.task}>
            <div className={css.left}>{taskCircle}</div>
            <div className={css.right}>
                <Text tag="span" size="major-sm" weight="semibold">
                    {name}
                </Text>
                {placeholder || description ? <div className={cn(css.description, {
        [css.placeholder]: Boolean(placeholder)
      })}>
                        {placeholder ? <Description width={20} height={20} color={'var(--display-onlight-tertiary)'} /> : null}
                        <Text size="body-xs" tag="span">
                            {description || placeholder}
                        </Text>
                    </div> : null}
            </div>
        </div>;
};
export { ProductUITaskView };